import { Add, ContentCopy, Delete } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import { useCRUD } from "../../api/crud";
import {
  buildClientInvoiceSettingsEndpoint,
  ERROR_SNACKBAR,
  INVOICES_ENDPOINT,
  INVOICES_LAST_ISSUED_ENDPOINT,
  INVOICE_KIND_OPTIONS,
  INVOICE_PAYMENT_METHOD_OPTIONS,
  NUMERATION_SCHEMA_ENDPOINT,
  SUCCESS_SNACKBAR,
  TENANT_SETTINGS_ENDPOINT,
  VAT_RATE_OPTIONS,
} from "../../consts";
import {
  ClientInvoiceSettings,
  Invoice,
  InvoiceItem,
  InvoiceItemFormValues,
} from "../../types/invoices";
import { NumerationSchema } from "../../types/numerationSchemas";
import {
  DECIMAL_FOUR_PLACES_INPUT_REGEX,
  formatDecimalForDisplay,
  formatDecimalForInput,
  formatNumber,
  toApiDecimal,
} from "../../utils";
import { resolveInvoiceDefaults } from "../../utils/invoiceDefaults";
import { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import CustomDatePicker from "../ui/customDatePicker/customDatePicker.component";
import ClientAutocomplete from "./clientAutocomplete.component";
import ReceiverFields from "./receiverFields.component";

/** DRF returns field errors as `{field: [{message, code}]}`. */
function getError(field: string, error: any): string | null {
  const fieldErrors = error?.[field];
  return Array.isArray(fieldErrors) && fieldErrors.length > 0
    ? fieldErrors[0]?.message ?? null
    : null;
}

function getItemError(index: number, field: string, error: any): string | null {
  const itemErrors = error?.items;
  if (!Array.isArray(itemErrors) || !itemErrors[index]) {
    return null;
  }
  return getError(field, itemErrors[index]);
}

const emptyItem = (vatRate: string = "23"): InvoiceItemFormValues => ({
  name: "",
  quantity: "1",
  unit: "szt.",
  price_net: "",
  vat_rate: vatRate,
});

/** An invoice's stored lines as the form edits them: decimals come back
 *  zero-padded and dotted, and are shown the way the user would type them. */
const itemsFromInvoice = (items: InvoiceItem[]): InvoiceItemFormValues[] =>
  items.map((item) => ({
    name: item.name,
    quantity: formatDecimalForInput(item.quantity),
    unit: item.unit,
    price_net: formatDecimalForInput(item.price_net),
    vat_rate: item.vat_rate,
  }));

/** Everything copying from another invoice overwrites, kept to undo it. */
interface CopySnapshot {
  kind: string;
  paymentMethod: string;
  numerationSchemaId: string;
  paymentTermDays: number;
  dueDate: string;
  dueDateTouched: boolean;
  placeOfIssue: string;
  notes: string;
  showReceiver: boolean;
  receiver: OptionRecord | null;
  receiverRole: string;
  items: InvoiceItemFormValues[];
}

/** A line nobody has typed into yet, so a new default rate may replace its own. */
const isPristineItem = (item: InvoiceItemFormValues) =>
  !item.name.trim() && !item.price_net.trim();

export interface InvoiceFormProps {
  id?: string | number;
  onSuccess?: () => void;
}

export default function InvoiceForm({ id, onSuccess }: InvoiceFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(INVOICES_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);

  const [serverErrors, setServerErrors] = useState<any>(null);
  const [client, setClient] = useState<OptionRecord | null>(null);
  // The client a defaults request was last made for, so a slow response for a
  // client the user has since replaced is dropped instead of applied.
  const defaultsRequestFor = useRef<string | number | null>(null);
  // The receiver (odbiorca) is optional and most invoices have none, so its
  // fields stay folded away until asked for or already set on the invoice.
  const [showReceiver, setShowReceiver] = useState<boolean>(false);
  const [receiver, setReceiver] = useState<OptionRecord | null>(null);
  const [receiverRole, setReceiverRole] = useState<string>("");
  const [paymentTermDays, setPaymentTermDays] = useState<number>(14);
  const [defaultVatRate, setDefaultVatRate] = useState<string>("23");
  const [numerationSchemas, setNumerationSchemas] = useState<NumerationSchema[]>(
    []
  );
  const [numerationSchemaId, setNumerationSchemaId] = useState<string>("");
  const [kind, setKind] = useState<string>("VAT");
  const [paymentMethod, setPaymentMethod] = useState<string>("6");
  const [issuedAt, setIssuedAt] = useState<string>(() =>
    dayjs().format("YYYY-MM-DD")
  );
  const [soldAt, setSoldAt] = useState<string>(() =>
    dayjs().format("YYYY-MM-DD")
  );
  const [dueDate, setDueDate] = useState<string>("");
  // Sale and due date follow the issue date until the user overrides them; once
  // they do, changing the issue date must not quietly undo their edit.
  const [soldAtTouched, setSoldAtTouched] = useState<boolean>(false);
  const [dueDateTouched, setDueDateTouched] = useState<boolean>(false);
  const [placeOfIssue, setPlaceOfIssue] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [items, setItems] = useState<InvoiceItemFormValues[]>([emptyItem()]);
  // The selected client's most recently issued invoice, offered to copy from.
  const [lastIssued, setLastIssued] = useState<Invoice | null>(null);
  const [copiedFrom, setCopiedFrom] = useState<Invoice | null>(null);
  const [copyUndo, setCopyUndo] = useState<CopySnapshot | null>(null);
  // The one decimal field being typed in, as "<index>.<field>": it shows its
  // raw value, every other one shows the grouped form.
  const [focusedDecimalField, setFocusedDecimalField] = useState<string | null>(
    null
  );

  useEffect(() => {
    axios
      .get(NUMERATION_SCHEMA_ENDPOINT)
      .then(({ data }) => {
        const schemas = Array.isArray(data?.results) ? data.results : data;
        setNumerationSchemas(Array.isArray(schemas) ? schemas : []);
      })
      .catch(() => {});
  }, []);

  // The payment term lives in tenant settings, so the due date can only be
  // filled in once it has been fetched. The default numeration schema comes
  // from the same place: the server would apply it anyway, so showing it
  // pre-selected lets the user see and change it before the invoice exists.
  useEffect(() => {
    if (id) {
      return;
    }

    getTenantSettings("settings").then((response) => {
      const settings = response?.data;
      if (!settings) {
        return;
      }

      if (settings.default_vat_rate) {
        applyDefaultVatRate(settings.default_vat_rate);
      }

      const schema = settings.default_invoice_numeration_schema;
      if (schema !== null && schema !== undefined) {
        setNumerationSchemaId((previous) => previous || String(schema));
      }

      const days = settings.invoice_payment_term_days;
      if (typeof days !== "number") {
        return;
      }

      setPaymentTermDays(days);
      setDueDate((previous) =>
        previous ? previous : dayjs().add(days, "day").format("YYYY-MM-DD")
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getTenantSettings]);

  useEffect(() => {
    if (!id) {
      return;
    }

    get(id).then((response) => {
      const invoice = response?.data;
      if (!invoice) {
        return;
      }

      setClient({ id: invoice.client_id ?? 0, name: invoice.client_name });
      if (invoice.receiver_id) {
        setShowReceiver(true);
        setReceiver({ id: invoice.receiver_id, name: invoice.receiver_name });
        setReceiverRole(invoice.receiver_role ?? "");
      }
      setKind(invoice.kind);
      setPaymentMethod(invoice.payment_method);
      setIssuedAt(invoice.issued_at ?? "");
      setSoldAt(invoice.sold_at ?? "");
      setDueDate(invoice.due_date ?? "");
      setSoldAtTouched(true);
      setDueDateTouched(true);
      setPlaceOfIssue(invoice.place_of_issue ?? "");
      setNotes(invoice.notes ?? "");
      setNumerationSchemaId(
        invoice.numeration_schema_id ? String(invoice.numeration_schema_id) : ""
      );
      setItems(itemsFromInvoice(invoice.items_detail ?? []));
    });
  }, [id, get]);

  // Looked up as soon as the client is known, so the copy button can say up
  // front whether there is anything to copy. A 404 just means there is not.
  const clientId = client?.id;
  useEffect(() => {
    setLastIssued(null);
    if (!clientId) {
      return;
    }

    let active = true;
    axios
      .get<Invoice>(INVOICES_LAST_ISSUED_ENDPOINT, {
        params: { client_id: clientId },
      })
      .then(({ data }) => active && setLastIssued(data))
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [clientId]);

  /** New lines take the rate, and so do the lines nobody has filled in yet. */
  const applyDefaultVatRate = (vatRate: string) => {
    setDefaultVatRate(vatRate);
    setItems((previous) =>
      previous.map((item) =>
        isPristineItem(item) ? { ...item, vat_rate: vatRate } : item
      )
    );
  };

  /**
   * Picking the client of a new invoice fills in that client's invoice
   * defaults, which already fall back to the workspace's where the client has
   * none - so switching to a client without its own settings resets the form
   * to the workspace defaults rather than keeping the previous client's.
   *
   * An existing invoice keeps what it has: its values were chosen already.
   */
  const handleClientChange = (value: OptionRecord | null) => {
    setClient(value);
    // An undo would bring back another client's document.
    setCopiedFrom(null);
    setCopyUndo(null);
    defaultsRequestFor.current = value?.id ?? null;
    if (id || !value) {
      return;
    }

    axios
      .get<ClientInvoiceSettings>(buildClientInvoiceSettingsEndpoint(value.id))
      .then(({ data }) => {
        if (defaultsRequestFor.current !== value.id) {
          return;
        }

        const defaults = resolveInvoiceDefaults(data);
        if (defaults.vatRate) {
          applyDefaultVatRate(defaults.vatRate);
        }
        if (defaults.paymentMethod) {
          setPaymentMethod(defaults.paymentMethod);
        }
        setNumerationSchemaId(
          defaults.numerationSchemaId !== null
            ? String(defaults.numerationSchemaId)
            : ""
        );
        if (defaults.paymentTermDays !== null) {
          setPaymentTermDays(defaults.paymentTermDays);
          if (!dueDateTouched && issuedAt) {
            setDueDate(
              dayjs(issuedAt)
                .add(defaults.paymentTermDays, "day")
                .format("YYYY-MM-DD")
            );
          }
        }
        setShowReceiver(!!defaults.receiver);
        setReceiver(defaults.receiver);
        setReceiverRole(defaults.receiverRole);
      })
      .catch(() => {});
  };

  /**
   * Fill the form in from another invoice: its kind, payment, numbering,
   * receiver, notes and lines. The dates are not copied - a new invoice is
   * issued now, not on the old one's dates - but the payment term is, read back
   * as the gap between the source's issue and due dates.
   *
   * Replaces whatever was entered, so the previous state is kept for an undo.
   */
  const copyFromInvoice = (source: Invoice) => {
    setCopyUndo({
      kind,
      paymentMethod,
      numerationSchemaId,
      paymentTermDays,
      dueDate,
      dueDateTouched,
      placeOfIssue,
      notes,
      showReceiver,
      receiver,
      receiverRole,
      items,
    });

    setKind(source.kind);
    setPaymentMethod(source.payment_method);
    setNumerationSchemaId(
      source.numeration_schema_id ? String(source.numeration_schema_id) : ""
    );
    const term = Math.max(
      dayjs(source.due_date).diff(dayjs(source.issued_at), "day"),
      0
    );
    setPaymentTermDays(term);
    if (issuedAt) {
      setDueDate(dayjs(issuedAt).add(term, "day").format("YYYY-MM-DD"));
      setDueDateTouched(false);
    }
    setPlaceOfIssue(source.place_of_issue ?? "");
    setNotes(source.notes ?? "");
    setShowReceiver(!!source.receiver_id);
    setReceiver(
      source.receiver_id
        ? { id: source.receiver_id, name: source.receiver_name }
        : null
    );
    setReceiverRole(source.receiver_role ?? "");
    setItems(itemsFromInvoice(source.items_detail));
    setServerErrors(null);
    setCopiedFrom(source);
  };

  const undoCopy = () => {
    if (!copyUndo) {
      return;
    }

    setKind(copyUndo.kind);
    setPaymentMethod(copyUndo.paymentMethod);
    setNumerationSchemaId(copyUndo.numerationSchemaId);
    setPaymentTermDays(copyUndo.paymentTermDays);
    setDueDate(copyUndo.dueDate);
    setDueDateTouched(copyUndo.dueDateTouched);
    setPlaceOfIssue(copyUndo.placeOfIssue);
    setNotes(copyUndo.notes);
    setShowReceiver(copyUndo.showReceiver);
    setReceiver(copyUndo.receiver);
    setReceiverRole(copyUndo.receiverRole);
    setItems(copyUndo.items);
    setCopyUndo(null);
    setCopiedFrom(null);
  };

  /** Moving the issue date carries the dates derived from it along, unless the
   *  user has already set those by hand. */
  const handleIssuedAtChange = (value: Dayjs | null) => {
    const formatted = value?.format("YYYY-MM-DD") || "";
    setIssuedAt(formatted);

    if (!value) {
      return;
    }

    if (!soldAtTouched) {
      setSoldAt(formatted);
    }
    if (!dueDateTouched) {
      setDueDate(value.add(paymentTermDays, "day").format("YYYY-MM-DD"));
    }
  };

  const updateItem = (
    index: number,
    field: keyof InvoiceItemFormValues,
    value: string
  ) => {
    setItems((previous) =>
      previous.map((item, position) =>
        position === index ? { ...item, [field]: value } : item
      )
    );
  };

  /** Field props for an item amount shown grouped ("1 234,56") when idle and
   *  raw while being typed in. */
  const decimalFieldProps = (
    index: number,
    field: "quantity" | "price_net"
  ) => {
    const key = `${index}.${field}`;
    return {
      value:
        focusedDecimalField === key
          ? items[index][field]
          : formatDecimalForDisplay(items[index][field]),
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        // Strip any grouping first: a value pasted from a grouped display
        // ("1 234,56") would otherwise fail the plain-decimal check.
        const nextValue = event.target.value.replace(/[\s\u00A0\u202F]/g, "");
        if (DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) {
          updateItem(index, field, nextValue);
        }
      },
      onFocus: () => setFocusedDecimalField(key),
      onBlur: () => setFocusedDecimalField(null),
      inputProps: { inputMode: "decimal" as const },
    };
  };

  const addItem = () =>
    setItems((previous) => [...previous, emptyItem(defaultVatRate)]);

  const removeItem = (index: number) =>
    setItems((previous) => previous.filter((_, position) => position !== index));

  /**
   * Preview only. The server recomputes every amount from quantity and unit
   * price when it saves, so this can never become the source of truth - it is
   * here so the user sees roughly what they are about to issue.
   */
  const previewTotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const quantity = parseFloat(toApiDecimal(item.quantity)) || 0;
      const price = parseFloat(toApiDecimal(item.price_net)) || 0;
      const rate = parseFloat(item.vat_rate) || 0;
      const net = quantity * price;
      return sum + net * (1 + rate / 100);
    }, 0);
  }, [items]);

  const hasReceiver = showReceiver && !!receiver;

  const removeReceiver = () => {
    setShowReceiver(false);
    setReceiver(null);
    setReceiverRole("");
  };

  // The role is not defaulted: it sets the buyer's JST/GV markers in KSeF, so
  // it has to be a deliberate choice rather than whatever was preselected.
  const isValid =
    !!client &&
    (!hasReceiver || !!receiverRole) &&
    items.length > 0 &&
    items.every((item) => item.name.trim() && item.price_net.trim());

  const handleSubmit = async () => {
    setServerErrors(null);

    const payload: Record<string, unknown> = {
      client_id: client?.id,
      receiver_id: hasReceiver ? receiver?.id : null,
      receiver_role: hasReceiver ? receiverRole : "",
      kind,
      payment_method: paymentMethod,
      place_of_issue: placeOfIssue || undefined,
      notes: notes || undefined,
      issued_at: issuedAt || undefined,
      sold_at: soldAt || undefined,
      due_date: dueDate || undefined,
      numeration_schema_id: numerationSchemaId || null,
      items: items.map((item) => ({
        ...item,
        quantity: toApiDecimal(item.quantity),
        price_net: toApiDecimal(item.price_net),
      })),
    };

    const result = id
      ? await update({ id, ...payload })
      : await create(payload);

    if (result?.success || result?.data?.id) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.();
      window.dispatchEvent(new Event("invoiceUpdated"));
      return;
    }

    setServerErrors(result?.errorData);
    enqueueSnackbar(
      result?.errorData?.message ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  return (
    <Stack spacing={2} sx={{ minWidth: { xs: "auto", md: 720 } }}>
      {/* Once a receiver is added the client is labelled as the buyer, so the
          two parties read apart. */}
      <ClientAutocomplete
        value={client}
        onChange={handleClientChange}
        label={showReceiver ? t("Buyer") : t("Client")}
        required
        error={getError("client_id", serverErrors)}
      />

      <Box>
        <Tooltip
          title={
            !client
              ? t("Select a client first")
              : lastIssued
                ? ""
                : t("This client has no issued invoices yet")
          }
        >
          {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => lastIssued && copyFromInvoice(lastIssued)}
              disabled={!lastIssued}
            >
              {t("Copy from last issued invoice")}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {copiedFrom && (
        <Alert
          severity="info"
          action={
            copyUndo && (
              <Button color="inherit" size="small" onClick={undoCopy}>
                {t("Undo")}
              </Button>
            )
          }
        >
          {t(
            "Filled in from invoice {{number}}. Dates were not copied - check the item descriptions for periods before saving.",
            { number: copiedFrom.number }
          )}
        </Alert>
      )}

      {showReceiver ? (
        <ReceiverFields
          receiver={receiver}
          onReceiverChange={setReceiver}
          role={receiverRole}
          onRoleChange={setReceiverRole}
          receiverError={getError("receiver_id", serverErrors)}
          roleError={getError("receiver_role", serverErrors)}
          action={
            <IconButton
              size="small"
              aria-label={t("Remove receiver")}
              onClick={removeReceiver}
              sx={{ mt: 0.5 }}
            >
              <Delete />
            </IconButton>
          }
        />
      ) : (
        <Box>
          <Button
            startIcon={<Add />}
            onClick={() => setShowReceiver(true)}
            size="small"
          >
            {t("Add receiver")}
          </Button>
        </Box>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          size="small"
          select
          fullWidth
          label={t("Invoice kind")}
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        >
          {INVOICE_KIND_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          fullWidth
          label={t("Payment method")}
          value={paymentMethod}
          onChange={(event) => setPaymentMethod(event.target.value)}
        >
          {INVOICE_PAYMENT_METHOD_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* The schema decides the number the invoice gets when it is issued, and
          it is read at issue time - so it can still be corrected on a draft. */}
      <TextField
        size="small"
        select
        fullWidth
        label={t("Numeration schema")}
        value={
          numerationSchemas.some(
            (schema) => String(schema.id) === numerationSchemaId
          )
            ? numerationSchemaId
            : ""
        }
        onChange={(event) => setNumerationSchemaId(event.target.value)}
        error={!!getError("numeration_schema_id", serverErrors)}
        helperText={
          getError("numeration_schema_id", serverErrors) ??
          t("Leave empty to use the default configured for the workspace.")
        }
      >
        <MenuItem value="">
          <em>{t("Workspace default")}</em>
        </MenuItem>
        {numerationSchemas.map((schema) => (
          <MenuItem key={schema.id} value={String(schema.id)}>
            {schema.name}
          </MenuItem>
        ))}
      </TextField>

      {/* Dates stay as "YYYY-MM-DD" strings in state - that is what the API
          takes. They are pre-filled with the same defaults the server would
          apply, so the user can see and adjust them rather than read about
          them in helper text. */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <CustomDatePicker
          label={t("Date of issue")}
          value={issuedAt ? dayjs(issuedAt) : null}
          onChange={handleIssuedAtChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
        <CustomDatePicker
          label={t("Date of sale")}
          value={soldAt ? dayjs(soldAt) : null}
          onChange={(value: Dayjs | null) => {
            setSoldAtTouched(true);
            setSoldAt(value?.format("YYYY-MM-DD") || "");
          }}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
        <CustomDatePicker
          label={t("Payment due")}
          value={dueDate ? dayjs(dueDate) : null}
          onChange={(value: Dayjs | null) => {
            setDueDateTouched(true);
            setDueDate(value?.format("YYYY-MM-DD") || "");
          }}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
      </Stack>

      <TextField
        size="small"
        label={t("Place of issue")}
        value={placeOfIssue}
        onChange={(event) => setPlaceOfIssue(event.target.value)}
      />

      <Divider />

      <Typography variant="subtitle2">{t("Invoice items")}</Typography>

      {items.map((item, index) => (
        <Stack
          key={index}
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems="flex-start"
        >
          <TextField
            size="small"
            label={t("Name")}
            value={item.name}
            onChange={(event) => updateItem(index, "name", event.target.value)}
            required
            sx={{ flex: 3 }}
            error={!!getItemError(index, "name", serverErrors)}
            helperText={getItemError(index, "name", serverErrors)}
          />
          <TextField
            size="small"
            label={t("Qty")}
            {...decimalFieldProps(index, "quantity")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t("Unit of measure")}
            value={item.unit}
            onChange={(event) => updateItem(index, "unit", event.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t("Net price")}
            {...decimalFieldProps(index, "price_net")}
            required
            sx={{ flex: 1.5 }}
            error={!!getItemError(index, "price_net", serverErrors)}
            helperText={getItemError(index, "price_net", serverErrors)}
          />
          <TextField
            size="small"
            select
            label={t("VAT rate")}
            value={item.vat_rate}
            onChange={(event) =>
              updateItem(index, "vat_rate", event.target.value)
            }
            sx={{ flex: 1.5, minWidth: 120 }}
          >
            {VAT_RATE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
          <IconButton
            size="small"
            aria-label={t("Remove item")}
            onClick={() => removeItem(index)}
            disabled={items.length === 1}
            // Nudged to sit on the centre line of the small inputs beside it.
            sx={{ mt: 0.5 }}
          >
            <Delete />
          </IconButton>
        </Stack>
      ))}

      {getError("items", serverErrors) && (
        <Typography color="error" variant="caption">
          {getError("items", serverErrors)}
        </Typography>
      )}

      <Box>
        <Button startIcon={<Add />} onClick={addItem} size="small">
          {t("Add item")}
        </Button>
      </Box>

      <TextField
        size="small"
        label={t("Notes")}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        multiline
        minRows={2}
      />

      <Divider />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body2" color="text.secondary">
          {t("Estimated gross total")}
        </Typography>
        <Typography variant="h6">{formatNumber(previewTotal)} zł</Typography>
      </Stack>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading || !isValid}
      >
        {id ? t("Save invoice") : t("Create invoice")}
      </Button>
    </Stack>
  );
}
